<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-pricing slice-pricing--default"
        data-testid="pricing-default-slice"
    >
        <div class="slice-content">
            <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" data-testid="pricing-default-title" />
            <PrismicRichText
                v-if="slice.primary.description"
                :field="slice.primary.description"
                class="slice-description"
                data-testid="pricing-default-description"
            />
            <div class="slice-body">
                <NuxtErrorBoundary>
                    <Swiper class="swiper-pricing" :breakpoints="swiperBreakpoints">
                        <SwiperSlide v-for="(pricingPlan, i) in slice.items" :key="i">
                            <PricingCard :pricing-plan="pricingPlan" :index="i" />
                        </SwiperSlide>
                    </Swiper>
                </NuxtErrorBoundary>
            </div>
            <div v-if="slice.primary.footerLinkUrl" class="footer-link">
                <NuxtLink :to="$withLocalePrefix(slice.primary.footerLinkUrl)" class="slice-footer__link" data-testid="pricing-default-footer-link">
                    {{ slice.primary.footerLinkLabel }}
                </NuxtLink>
            </div>

            <div v-if="slice.primary.footerText" class="slice-footer" data-testid="pricing-default-footer-text">
                <PrismicRichText :field="slice.primary.footerText" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {PricingSlice} from '@prismicio-types';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import {useComponentTracking} from '#imports';
    import PricingCard from '../components/PricingCard.vue';

    type PricingDefaultInZone = Extract<PricingSlice, {variation: 'default'}>;
    defineProps(getSliceComponentProps<PricingDefaultInZone>());

    const section = useTemplateRef('section');
    const swiperBreakpoints = {
        0: {slidesPerView: 1.15},
        768: {slidesPerView: 2.25},
        1024: {slidesPerView: 3},
    };

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    .slice-pricing {
        &--default {
            .swiper-pricing {
                margin: 0 calc(-1 * var(--joy-core-spacing-4));
                .swiper-wrapper {
                    align-items: stretch;
                }
                .swiper-slide {
                    min-width: 350px;
                    width: 450px;
                    height: auto;
                    padding: var(--joy-core-spacing-4);
                }
                @include mq.screen_lg(min) {
                    .swiper-wrapper {
                        flex-wrap: wrap;
                        justify-content: center;
                    }
                }
            }
            .pricing-plan {
                max-width: none;
                height: 100%;
            }
            .footer-link {
                margin-top: var(--joy-core-spacing-8);
                a,
                a:visited {
                    color: var(--joy-color-secondary-30);
                    text-decoration: underline;
                    font-weight: var(--joy-font-weight-bold);
                }
            }
            .slice-footer {
                text-align: left;
            }
        }
    }
</style>
